// src/services/firestore-service.js
import { db, auth } from '../config/firebase-config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { isOldModalityFormat, migrateModalities } from '../utils/migrateModalities';

// ============================================================================
// USER ID HELPER
// ============================================================================

/**
 * Get current user's UID from Firebase Auth or localStorage fallback
 * @returns {string|null} User ID
 */
export const getCurrentUserId = () => {
  // Try Firebase auth first (authoritative)
  if (auth.currentUser) {
    return auth.currentUser.uid;
  }

  // Fallback to localStorage (during auth initialization)
  return localStorage.getItem('userId');
};

// ============================================================================
// WEBSITE DATA CRUD
// ============================================================================

export const getWebsiteDraft = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().website_draft) {
      return docSnap.data().website_draft;
    }
    return null;
  } catch (error) {
    console.error('Error reading website_draft:', error);
    throw error;
  }
};

export const getWebsitePublished = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().website_published) {
      return docSnap.data().website_published;
    }
    return null;
  } catch (error) {
    console.error('Error reading website_published:', error);
    throw error;
  }
};

export const getWebsiteLastPublished = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().website_last_published) {
      // Return timestamp as Date object if it exists
      const timestamp = docSnap.data().website_last_published;
      return timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    }
    return null;
  } catch (error) {
    console.error('Error reading website_last_published:', error);
    throw error;
  }
};

export const saveWebsiteDraft = async (uid, draftData) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      website_draft: draftData,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving website_draft:', error);
    throw error;
  }
};

export const saveWebsitePublished = async (uid, publishedData) => {
  try {
    const docRef = doc(db, 'practitioners', uid);

    // Single atomic write — updateDoc replaces top-level field values entirely
    // (not a deep merge). Old Base64 data in website_draft/website_published is
    // overwritten in one operation with no race condition window.
    try {
      await updateDoc(docRef, {
        website_draft: publishedData,
        website_published: publishedData,
        website_last_published: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      if (err.code === 'not-found') {
        // Document doesn't exist yet (brand-new user) — create it.
        await setDoc(docRef, {
          website_draft: publishedData,
          website_published: publishedData,
          website_last_published: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });
      } else {
        throw err;
      }
    }

    console.log('✅ saveWebsitePublished: atomic write — website_draft + website_published updated');
  } catch (error) {
    console.error('Error saving website_published:', error);
    throw error;
  }
};

export const updateWebsiteDraftField = async (uid, fieldPath, value) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const updateData = {
      [`website_draft.${fieldPath}`]: value,
      updatedAt: serverTimestamp()
    };
    await setDoc(docRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error updating website_draft field:', error);
    throw error;
  }
};

// ============================================================================
// PROFILE DATA CRUD
// ============================================================================

export const getProfile = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().profile) {
      const profile = docSnap.data().profile;

      // One-time migration: convert old flat modality array to new structured format
      if (isOldModalityFormat(profile.modalities)) {
        console.log('🔄 MIGRATE getProfile: old modality format detected — migrating');
        const migratedProfile = { ...profile, modalities: migrateModalities(profile.modalities) };
        // Fire-and-forget: persist migrated data so this only runs once
        saveProfile(uid, migratedProfile).catch(err =>
          console.warn('⚠️ Failed to save migrated profile:', err)
        );
        return migratedProfile;
      }

      return profile;
    }
    return null;
  } catch (error) {
    console.error('Error reading profile:', error);
    throw error;
  }
};

export const saveProfile = async (uid, profileData) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      profile: profileData,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving profile:', error);
    throw error;
  }
};

// ============================================================================
// SERVICES CRUD
// ============================================================================

export const getServices = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().services) {
      return docSnap.data().services;
    }
    return [];
  } catch (error) {
    console.error('Error reading services:', error);
    throw error;
  }
};

export const saveServices = async (uid, servicesArray) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      services: servicesArray,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving services:', error);
    throw error;
  }
};

// ============================================================================
// AVAILABILITY CRUD
// ============================================================================

export const getAvailability = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().availability) {
      return docSnap.data().availability;
    }
    return null;
  } catch (error) {
    console.error('Error reading availability:', error);
    throw error;
  }
};

export const saveAvailability = async (uid, availabilityData) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      availability: availabilityData,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving availability:', error);
    throw error;
  }
};

// ============================================================================
// CONTENT CRUD (for backward compatibility)
// ============================================================================

export const getFAQs = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().website_draft?.content?.faqs) {
      return docSnap.data().website_draft.content.faqs;
    }
    return [];
  } catch (error) {
    console.error('Error reading FAQs:', error);
    throw error;
  }
};

export const getTestimonials = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().website_draft?.content?.testimonials) {
      return docSnap.data().website_draft.content.testimonials;
    }
    return [];
  } catch (error) {
    console.error('Error reading testimonials:', error);
    throw error;
  }
};

export const getGalleryLayout = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().website_draft?.content?.gallery?.layout) {
      return docSnap.data().website_draft.content.gallery.layout;
    }
    return 'justified';
  } catch (error) {
    console.error('Error reading gallery layout:', error);
    throw error;
  }
};

// ============================================================================
// OPERATIONAL DATA CRUD
// ============================================================================

export const getOnboardingData = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().onboarding) {
      return docSnap.data().onboarding;
    }
    return null;
  } catch (error) {
    console.error('Error reading onboarding data:', error);
    throw error;
  }
};

export const saveOnboardingData = async (uid, data) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      onboarding: data,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving onboarding data:', error);
    throw error;
  }
};

export const getWizardData = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().wizard) {
      return docSnap.data().wizard;
    }
    return null;
  } catch (error) {
    console.error('Error reading wizard data:', error);
    throw error;
  }
};

export const saveWizardData = async (uid, data) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      wizard: data,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving wizard data:', error);
    throw error;
  }
};

export const getStripeConnect = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().stripe) {
      return docSnap.data().stripe;
    }
    return null;
  } catch (error) {
    console.error('Error reading stripe data:', error);
    throw error;
  }
};

export const saveStripeConnect = async (uid, stripeData) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      stripe: stripeData,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving stripe data:', error);
    throw error;
  }
};

// ============================================================================
// MIGRATION HELPER
// ============================================================================

/**
 * One-time migration from localStorage to Firestore
 * Migrates all practitioner data including website, profile, services, etc.
 * Gallery images stay in localStorage (Phase D migration to Firebase Storage)
 *
 * @param {string} uid - User ID
 * @returns {Promise<{success: boolean, migratedCount: number}>}
 */
export const migrateLocalStorageToFirestore = async (uid) => {
  try {
    console.log('🔄 Starting localStorage → Firestore migration for user:', uid);

    const migrations = [];

    // Migrate unified website data
    const websiteDraft = localStorage.getItem('practitioner_website_draft');
    if (websiteDraft) {
      const parsed = JSON.parse(websiteDraft);

      // Remove gallery images from migration (they stay in localStorage until Phase D)
      if (parsed.content?.gallery?.images) {
        console.log('ℹ️ Keeping gallery images in localStorage (Phase D will migrate to Storage)');
        // Keep layout but remove images from Firestore migration
        const galleryWithoutImages = {
          ...parsed.content.gallery,
          images: [] // Empty array - images stay in localStorage
        };
        parsed.content.gallery = galleryWithoutImages;
      }

      migrations.push({ field: 'website_draft', data: parsed });
    }

    const websitePublished = localStorage.getItem('practitioner_website_published');
    if (websitePublished) {
      const parsed = JSON.parse(websitePublished);

      // Remove gallery images from published too
      if (parsed.content?.gallery?.images) {
        const galleryWithoutImages = {
          ...parsed.content.gallery,
          images: []
        };
        parsed.content.gallery = galleryWithoutImages;
      }

      migrations.push({ field: 'website_published', data: parsed });
    }

    const lastPublished = localStorage.getItem('practitioner_website_last_published');
    if (lastPublished) {
      migrations.push({ field: 'website_last_published', data: new Date(lastPublished) });
    }

    // Migrate profile
    const profile = localStorage.getItem('practitioner_profile');
    if (profile) {
      migrations.push({ field: 'profile', data: JSON.parse(profile) });
    }

    // Migrate availability
    const availability = localStorage.getItem('practitioner_availability');
    if (availability) {
      migrations.push({ field: 'availability', data: JSON.parse(availability) });
    }

    // Migrate services
    const services = localStorage.getItem('practitioner_services');
    if (services) {
      migrations.push({ field: 'services', data: JSON.parse(services) });
    }

    // Migrate stripe
    const stripe = localStorage.getItem('practitioner_stripe_connect');
    if (stripe) {
      migrations.push({ field: 'stripe', data: JSON.parse(stripe) });
    }

    // Migrate wizard data
    const wizard = localStorage.getItem('practitioner_guided_setup');
    if (wizard) {
      migrations.push({ field: 'wizard', data: JSON.parse(wizard) });
    }

    const wizardCompleted = localStorage.getItem('wizard_completed');
    if (wizardCompleted) {
      migrations.push({ field: 'wizard_completed', data: wizardCompleted === 'true' });
    }

    // Migrate onboarding data
    const onboardingData = localStorage.getItem('onboarding_data');
    const onboardingProgress = localStorage.getItem('onboarding_progress');
    const onboardingCompleted = localStorage.getItem('onboarding_completed');

    if (onboardingData || onboardingProgress || onboardingCompleted) {
      migrations.push({
        field: 'onboarding',
        data: {
          data: onboardingData ? JSON.parse(onboardingData) : null,
          progress: onboardingProgress ? parseInt(onboardingProgress) : 0,
          completed: onboardingCompleted === 'true'
        }
      });
    }

    // Migrate tour requested
    const tourRequested = localStorage.getItem('tour_requested');
    if (tourRequested) {
      migrations.push({ field: 'tour_requested', data: tourRequested === 'true' });
    }

    // Execute all migrations in a single setDoc call
    if (migrations.length > 0) {
      const docRef = doc(db, 'practitioners', uid);
      const updateData = {};

      migrations.forEach(({ field, data }) => {
        updateData[field] = data;
      });

      updateData.migratedAt = serverTimestamp();
      updateData.updatedAt = serverTimestamp();

      await setDoc(docRef, updateData, { merge: true });

      console.log(`✅ Migration complete: ${migrations.length} fields migrated to Firestore`);
      console.log('ℹ️ Gallery images remain in localStorage (Phase D: Firebase Storage)');
      return { success: true, migratedCount: migrations.length };
    } else {
      console.log('ℹ️ No data to migrate from localStorage');
      return { success: true, migratedCount: 0 };
    }
  } catch (error) {
    console.error('❌ Migration error:', error);
    throw error;
  }
};

// ============================================================================
// BUSINESS INFO CRUD
// ============================================================================

/**
 * Get practitioner business info from Firestore
 * @param {string} uid - User ID
 * @returns {Promise<Object|null>}
 */
export const getBusinessInfo = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().business) {
      return docSnap.data().business;
    }
    return null;
  } catch (error) {
    console.error('Error reading business info:', error);
    throw error;
  }
};

/**
 * Save practitioner business info to Firestore
 * @param {string} uid - User ID
 * @param {Object} businessData - Business information
 */
export const saveBusinessInfo = async (uid, businessData) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      business: businessData,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving business info:', error);
    throw error;
  }
};

// ============================================================================
// NOTIFICATION PREFERENCES CRUD
// ============================================================================

/**
 * Get notification preferences from Firestore
 * @param {string} uid - User ID
 * @returns {Promise<Object|null>}
 */
export const getNotificationPreferences = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().notificationPreferences) {
      return docSnap.data().notificationPreferences;
    }
    return null;
  } catch (error) {
    console.error('Error reading notification preferences:', error);
    throw error;
  }
};

/**
 * Save notification preferences to Firestore
 * @param {string} uid - User ID
 * @param {Object} preferences - Notification preferences
 */
export const saveNotificationPreferences = async (uid, preferences) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      notificationPreferences: preferences,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving notification preferences:', error);
    throw error;
  }
};

// ============================================================================
// PROFILE STATUS CRUD
// ============================================================================

/**
 * Get profile operational status (paused state) from Firestore
 * @param {string} uid - User ID
 * @returns {Promise<Object|null>}
 */
export const getProfileStatus = async (uid) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().profileStatus) {
      return docSnap.data().profileStatus;
    }
    return null;
  } catch (error) {
    console.error('Error reading profile status:', error);
    throw error;
  }
};

/**
 * Update profile operational status (paused state) in Firestore
 * @param {string} uid - User ID
 * @param {Object} status - { paused: boolean, pausedDate: string|null }
 */
export const updateProfileStatus = async (uid, status) => {
  try {
    const docRef = doc(db, 'practitioners', uid);
    await setDoc(docRef, {
      profileStatus: status,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving profile status:', error);
    throw error;
  }
};

export default {
  getCurrentUserId,
  getWebsiteDraft,
  getWebsitePublished,
  getWebsiteLastPublished,
  saveWebsiteDraft,
  saveWebsitePublished,
  updateWebsiteDraftField,
  getProfile,
  saveProfile,
  getServices,
  saveServices,
  getAvailability,
  saveAvailability,
  getFAQs,
  getTestimonials,
  getGalleryLayout,
  getOnboardingData,
  saveOnboardingData,
  getWizardData,
  saveWizardData,
  getStripeConnect,
  saveStripeConnect,
  getBusinessInfo,
  saveBusinessInfo,
  getNotificationPreferences,
  saveNotificationPreferences,
  getProfileStatus,
  updateProfileStatus,
  migrateLocalStorageToFirestore
};
